import {effect, inject, signal} from '@angular/core';
import {StorePlugin} from 'signalstory';
import {SignalStorePersistenceDB} from './db';

// interface for the store that uses the idb persistence plugin
export interface IdbPersistenceStore<TState extends object> {
	onStoreLoaded?(state: TState): void;

	onStoreUpdated?(state: TState): void;
}

/**
 * Use the idb persistence plugin to save the state to the database
 * @param signalStorePersistenceDB - The signal store persistence db
 * @returns the plugin
 */
export function useIdbPersistence<TState extends object>(signalStorePersistenceDB: SignalStorePersistenceDB = inject(SignalStorePersistenceDB)): StorePlugin {
	let storeName!: string;
	let initialState!: TState;
	const hasLoaded$ = signal<TState | null>(null);
	return {
		pluginId: 'idb-persistence',
		precedence: 100,
		init: store => {
			storeName = store.name;
			initialState = store.config.initialState;
			// load the state from the database
			signalStorePersistenceDB.loadStore(storeName, initialState).then(state => {
				hasLoaded$.set(state);
			});
			// wait for the state to be loaded from the database
			const loadEffect$ = effect(() => {
				const state = hasLoaded$();
				if (state) {
					store.set(state);
					(store as IdbPersistenceStore<TState>).onStoreLoaded?.(state);
					loadEffect$.destroy();
				}
			});
		},
		postprocessCommand: (store, command) => {
			// save the state to the database
			if (hasLoaded$()) {
				signalStorePersistenceDB.saveStore(storeName, store.state()).then(() => command);
				(store as IdbPersistenceStore<TState>).onStoreUpdated?.(store.state());
			}
		},
		others: {
			resetLocalPersist: () => {
				signalStorePersistenceDB.deleteStore(storeName);
			},
		},
	};
}
